import React, { useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import { colors } from '@constants/colors';
import { Icon } from '@mui';
import DefaultDate from '@shared/formatted-date-helper';
import { PriorityIcon } from '@utils/icon-utils';
import { getConfigValue } from '@utils/list-item-utils';
import './list-item.scss';

const NewBaseListItem = ({
  // Data props
  category_dict,
  end_date,
  icon,
  id,
  name,
  start_date,
  type_name,
  // Customization props
  entityTypeName = '',
  headerIcon,
  onClick,
  onMouseEnter,
  onMouseLeave,
  renderContent,
  renderFooter,
  renderType,
  renderTypeIcon,
  styleNames = '',
  titleName
}) => {
  const getPriority = useCallback(
    () => category_dict && category_dict.find(category => category.name === 'priority'),
    [category_dict]
  );

  const getDateContent = useCallback(() => {
    if (start_date && end_date) {
      return (
        <span><DefaultDate value={start_date} />&ndash;<DefaultDate value={end_date} /></span>
      );
    } else if (start_date || end_date) {
      return (
        start_date ?
          <span><DefaultDate value={start_date} /></span> :
          <span><DefaultDate value={end_date} /></span>
      );
    }
    return 'Start/end dates unspecified.';
  }, [start_date, end_date]);

  const renderPriorityCell = useCallback(() => {
    const priority = getPriority();
    if (!priority) {
      return null;
    }
    return (
      <div styleName="footer-icon">
        <span><PriorityIcon priority={priority.id} /></span>
        <span>&nbsp;&nbsp;&bull;&nbsp;&nbsp;</span>
      </div>
    );
  }, [getPriority]);

  const renderDate = useCallback(
    () => <div styleName="footer-date">{getDateContent()}</div>,
    [getDateContent]
  );

  const renderDefaultTypeIcon = useCallback(typeIconName => (
    <div styleName="icon type-icon">
      <Icon
        color={colors.opacity.black0_6}
        aria-hidden="true"
        style={{ fontSize: 16 }}
      >
        {typeIconName}
      </Icon>
    </div>
  ), []);

  const resolvedTitleName = typeof titleName !== 'undefined' && titleName !== null
    ? titleName
    : getConfigValue(type_name, 'title', { type_name, name, id });
  const resolvedHeaderIcon = typeof headerIcon !== 'undefined' && headerIcon !== null
    ? headerIcon
    : icon;

  const renderEntityIcon = () => (
    <div styleName={`icon entity-icon entity-${entityTypeName}`}>
      {resolvedHeaderIcon && <img src={resolvedHeaderIcon} alt="" />}
    </div>
  );

  const renderTitle = () => (
    <div styleName="title">
      {resolvedTitleName}
    </div>
  );

  const renderHeaderId = () => (
    <div styleName="id">
      ID {id}
    </div>
  );

  const renderName = () => (
    <div styleName="name">{name}</div>
  );

  const renderHeader = () => (
    <div styleName="header">
      {renderEntityIcon()}
      {renderTitle()}
      {renderTypeIcon ? renderTypeIcon() : renderDefaultTypeIcon()}
      {renderHeaderId()}
    </div>
  );

  const renderDefaultContent = () => (
    <div styleName="content">
      {renderName()}
    </div>
  );

  const renderDefaultFooter = () => (
    <div styleName="footer">
      {renderPriorityCell()}
      {renderDate()}
      {renderType ? renderType() : null}
    </div>
  );

  if (!id) {
    return null;
  }

  const handleKeyDown = event => {
    if ((event.key === 'Enter' || event.key === ' ') && onClick) {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <div
      key={id}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      role="button"
      tabIndex={0}
      aria-label={name}
      styleName={`list-item ${styleNames}`}
    >
      {renderHeader()}
      {renderContent ? renderContent() : renderDefaultContent()}
      {renderFooter ? renderFooter() : renderDefaultFooter()}
    </div>
  );
};

NewBaseListItem.propTypes = {
  category_dict: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  end_date: PropTypes.string,
  entityTypeName: PropTypes.string,
  headerIcon: PropTypes.string,
  icon: PropTypes.string,
  id: PropTypes.number,
  name: PropTypes.string,
  onClick: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  renderContent: PropTypes.func,
  renderFooter: PropTypes.func,
  renderType: PropTypes.func,
  renderTypeIcon: PropTypes.func,
  start_date: PropTypes.string,
  styleNames: PropTypes.string,
  titleName: PropTypes.string,
  type_name: PropTypes.string
};

export default memo(NewBaseListItem);
